import { memo, useCallback } from 'react';
import classNames from 'classnames';
import { ComponentType } from '@vgtv/api-client/lib/component';

import { isSvpCategory } from '../../../modules/helpers';
import { CategoryItem } from '../../PageComponents/common/CategoryItem/CategoryItem';
import { AssetItem } from '../../PageComponents/common/AssetItem/AssetItem';
import { Slider } from '../../Slider/Slider';
import { useAssetItemTracking } from '../../PageComponents/common/useAssetItemTracking';
import { useCategoryItemTracking } from '../../PageComponents/common/useCategoryItemTracking';

import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { FC, VFC } from 'react';

import * as styles from './MixedSlider.module.scss';

type MixedSliderThumbnailProps = {
  entity: SvpAssetAdditional | SvpCategoryAdditional;
  onClick?: () => void;
  onHover?: () => void;
};

const MixedSliderThumbnail: VFC<MixedSliderThumbnailProps> = (props) => {
  const { entity, onHover, onClick } = props;

  const commonProps = {
    componentType: ComponentType.LIST,
    skipMetadata: true,
    size: 'medium',
    onClick,
    onHover,
  } as const;

  if (isSvpCategory(entity)) {
    return <CategoryItem {...commonProps} category={entity} />;
  }

  return <AssetItem {...commonProps} asset={entity} />;
};

type MixedSliderProps = {
  items: (SvpAssetAdditional | SvpCategoryAdditional)[];
  currentIndex: number;
  onHover?: (index?: number) => void;
  trackElementClick: (buttonType: string) => void;
  pageComponentIndex?: number;
};

const MixedSliderBase: FC<MixedSliderProps> = (props) => {
  const {
    items,
    onHover,
    currentIndex,
    pageComponentIndex,
    trackElementClick,
  } = props;

  const { trackAssetClick } = useAssetItemTracking({
    elementType: 'MixedSlider',
    pageComponentIndex,
  });

  const { trackCategoryClick } = useCategoryItemTracking({
    elementType: 'MixedSlider',
    pageComponentIndex,
  });

  const trackEntityClick = useCallback(
    (item: SvpAssetAdditional | SvpCategoryAdditional, index: number) => {
      if (isSvpCategory(item)) {
        trackCategoryClick(item, index);
      } else {
        trackAssetClick(item, index);
      }
      trackElementClick('Thumbnail');
    },
    [trackAssetClick, trackCategoryClick, trackElementClick]
  );

  return (
    <Slider className={styles.slider}>
      {items.map((entity, itemIndex) => {
        return (
          <div
            key={`${isSvpCategory(entity) ? 'category' : 'asset'}:${entity.id}`}
            className={classNames(styles.wrapper, {
              [styles.active]: itemIndex === currentIndex,
            })}
          >
            <MixedSliderThumbnail
              entity={entity}
              onClick={() => trackEntityClick(entity, itemIndex)}
              onHover={onHover ? () => onHover?.(itemIndex) : undefined}
            />
          </div>
        );
      })}
    </Slider>
  );
};

export const MixedSlider = memo(MixedSliderBase);
